/**
 * The rules a promo link is read by — the shape of a code, the shape of its `?source=` tag, and how
 * one is held between a signup and the moment the account exists to receive credits.
 *
 * Pure decision functions, no DOM and no runtime imports, so they can be unit-tested; the thin
 * storage wrapper is at the bottom. Deliberately the same shape as `referral-capture.ts`, which
 * solves the same problem for invite links — a value off a URL that cannot be spent until an
 * identity exists.
 *
 * Known limit, accepted, and the same one the referral has: this is bound to a BROWSER. Someone who
 * opens the link on a desktop and then finishes onboarding inside the phone app leaves no way to
 * read it, so that grant is not made. Closing it would need the pending code stored server-side on
 * the recipient, which nothing owns today.
 */

/** A code is a plain token: it travels in a path segment and is typed by hand. */
const PROMO_CODE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

/** A campaign channel tag. Mirrors `PromoCodes.SOURCE_TOKEN` on the service, which drops the rest. */
const PROMO_SOURCE = /^[A-Za-z0-9_.-]{1,64}$/;

export const PROMO_STORAGE_KEY = 'symphony-promo';

/**
 * Long enough that a slow onboarding keeps the grant, short enough that a shared machine forgets.
 * Matched to the referral's window for the same reasons — and it is only an upper bound: a code's
 * own `validUntil` on the service is what actually decides, and it is usually days, not weeks.
 */
export const PROMO_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface StoredPromo {
  code: string;
  /** Where the link was handed out (`?source=linkedin`), or '' when it carried no tag. */
  source: string;
  /** Who this was captured for. '' when the caller could not be identified at capture time. */
  userId: string;
  capturedAt: number;
}

/**
 * The code, lowercased, or null. Checked before it leaves the client for the same reason the
 * referral's is — not authentication (the value is public either way), but so that a junk path
 * segment is never stored and never retried for the whole TTL.
 */
export function normalizePromoCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toLowerCase();
  return PROMO_CODE.test(code) ? code : null;
}

/** The `?source=` tag, or '' when there wasn't a usable one. A bad tag costs attribution, never the grant. */
export function normalizePromoSource(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const source = raw.trim();
  return PROMO_SOURCE.test(source) ? source.toLowerCase() : '';
}

/** What to persist when a promo link lands, or null when there is nothing worth keeping. */
export function promoToStore({ code, source, userId, now }: { code: unknown; source: unknown; userId: string; now: number }): StoredPromo | null {
  const normalized = normalizePromoCode(code);
  if (!normalized) return null;
  return { code: normalized, source: normalizePromoSource(source), userId, capturedAt: now };
}

/**
 * The code to spend now, or null. Rejects an expired record, and one captured for a different
 * signed-in user — otherwise the next person to use this browser collects a stranger's campaign. A
 * record with no userId stays spendable: we could not identify the caller at capture, and the
 * payout is capped at one per account per code regardless.
 */
export function promoToSpend({ stored, userId, now }: { stored: StoredPromo | null; userId: string; now: number }): StoredPromo | null {
  if (!stored) return null;
  if (!normalizePromoCode(stored.code)) return null;
  if (now - stored.capturedAt > PROMO_TTL_MS) return null;
  if (stored.userId && userId && stored.userId !== userId) return null;
  return stored;
}

/** Parses whatever is in storage, tolerating anything a previous version or a user may have left. */
export function parseStoredPromo(rawJson: string | null): StoredPromo | null {
  if (!rawJson) return null;
  try {
    const value = JSON.parse(rawJson) as Partial<StoredPromo>;
    const code = normalizePromoCode(value?.code);
    if (!code || typeof value.capturedAt !== 'number') return null;
    return {
      code,
      source: normalizePromoSource(value.source),
      userId: typeof value.userId === 'string' ? value.userId : '',
      capturedAt: value.capturedAt,
    };
  } catch {
    return null;
  }
}

// ---- storage wrapper. Every path is guarded: Safari private mode throws on write, and a promo is
// never worth breaking a signup over.

export function savePromo(record: StoredPromo): void {
  try {
    window.localStorage.setItem(PROMO_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // No storage (private mode, quota, disabled) — the code is simply not held.
  }
}

export function loadPromo(): StoredPromo | null {
  try {
    return parseStoredPromo(window.localStorage.getItem(PROMO_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function clearPromo(): void {
  try {
    window.localStorage.removeItem(PROMO_STORAGE_KEY);
  } catch {
    // Nothing to do — see savePromo.
  }
}
